import { useSelector } from "react-redux";
import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";

import {
  GetNearbyWorkersParams,
  NearbyWorkerSearchResult,
  useGetNearbyWorkers,
} from "./useGetNearbyWorkers";
import { isDefined } from "@clipboard-health/util-ts";
import { UseModalState } from "@src/lib/ui-react";
import { SessionType } from "src/modules/interface";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import { NearbyWorker, NearbyWorkerResult } from "./NearbyWorkerResult";
import { useDebouncedValue } from "@src/appV2/lib/utils/useDebouncedValue";
import {
  useGetWorkerDetailsAtWorkplace,
  WorkerCategory,
  WorkerDetailsAtWorkplaceParams,
} from "@src/appV2/Professionals/api/useGetWorkerDetailsAtWorkplace";
import { useShiftInviteContext } from "../Context/shiftInviteContext";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";

export interface ShiftInviteNearbyWorkersProps {
  setSelectedWorker: (worker: NearbyWorkerSearchResult | undefined) => void;
  workerType: string;
  setActiveStep: (step: number) => void;
  selectedWorker: NearbyWorkerSearchResult | undefined;
  modalState: UseModalState;
  onClickNext: () => void;
}
export function ShiftInviteNearbyWorkers(props: ShiftInviteNearbyWorkersProps) {
  const { setSelectedWorker, workerType, setActiveStep, selectedWorker, modalState, onClickNext } =
    props;
  const [value, setValue] = useState<NearbyWorkerSearchResult | undefined>(selectedWorker);
  const [inputValue, setInputValue] = useState<string>(selectedWorker?.fullName || "");
  const { profile, user, admin } = useSelector((state: SessionType) => state.session);
  const workplaceId = profile?.userId;
  const currentValue = selectedWorker || value;
  const currentInputValue = selectedWorker?.fullName || inputValue;
  const [debouncedInputValue] = useDebouncedValue(currentInputValue);
  const { addUserJourneyEvent, logUserJourney } = useShiftInviteContext();

  const {
    isStarredProfessionalEnabled,
    workplaceHasStarredProfessionals,
    workplaceStarredProfessionalWorkerIds,
  } = useWorkplaceStarredProfessionals({ workplaceId });

  const starredProfessionalsQuery = useMemo<WorkerDetailsAtWorkplaceParams>(
    () => ({
      category: WorkerCategory.PAST,
      facilityId: workplaceId,
      search: debouncedInputValue,
      sort: "-shifts.lastShift.start",
      workerType,
      workerIds: workplaceStarredProfessionalWorkerIds,
      pageSize: 10,
    }),
    [workplaceId, workerType, debouncedInputValue, workplaceStarredProfessionalWorkerIds]
  );
  const { data: starredProfessionalsData } = useGetWorkerDetailsAtWorkplace(
    starredProfessionalsQuery,
    {
      enabled: isStarredProfessionalEnabled && workplaceHasStarredProfessionals,
    }
  );

  const nearbyWorkersQuery = useMemo<GetNearbyWorkersParams>(
    () => ({
      workplaceId,
      searchKey: debouncedInputValue,
      workerType,
    }),
    [workplaceId, debouncedInputValue, workerType]
  );
  const { data: shiftInviteNearbyWorkers, isLoading } = useGetNearbyWorkers(nearbyWorkersQuery);

  const inviteWorkersList = useMemo<NearbyWorker[]>(() => {
    const starredProfessionalsWorkers = starredProfessionalsData?.workers.map<NearbyWorker>(
      (w) => ({
        fullName: w.name,
        phoneNumber: w.phone,
        workerId: w.userId,
        isStarred: true,
      })
    );

    const workerIdsToExcludeFromNearbyWorkers = new Set(
      starredProfessionalsWorkers?.map((worker) => worker.workerId) ?? []
    );

    const filteredNearbyWorkers = shiftInviteNearbyWorkers?.filter(
      (worker) => !workerIdsToExcludeFromNearbyWorkers.has(worker.workerId)
    );

    const filteredNearbyWorkersList = filteredNearbyWorkers ?? [];
    const starredProfessionalsWorkersList = starredProfessionalsWorkers ?? [];

    return [...starredProfessionalsWorkersList, ...filteredNearbyWorkersList].slice(0, 10);
  }, [shiftInviteNearbyWorkers, starredProfessionalsData?.workers]);

  const segmentProperties = {
    workplaceId,
    workerType,
    ...(admin ? { adminUserId: user?._id } : { workplaceUserId: user?._id }),
    ...selectedWorker,
  };

  const BACK_BUTTON = "Back Button (Nearby Workers Screen)";
  const NEXT_BUTTON = "Next Button (Nearby Workers Screen)";
  const SEARCH_INPUT = "Search Nearby Workers Input";

  return (
    <>
      <Autocomplete
        id="get-nearby-workers"
        getOptionLabel={(option) => (typeof option === "string" ? option : option.fullName)}
        getOptionKey={(option) => (typeof option === "string" ? option : option.workerId)}
        filterOptions={(options) => options}
        options={inviteWorkersList}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={currentValue ?? null}
        isOptionEqualToValue={(option, valueSelected) => option.workerId === valueSelected.workerId}
        noOptionsText={
          currentInputValue ? "No Professional found" : "Type to search for Professionals"
        }
        loadingText={
          currentInputValue ? "Searching for Professionals" : "Type to search for Professionals"
        }
        loading={isLoading}
        onChange={(_event, newValue) => {
          if (!isDefined(newValue)) {
            setValue(undefined);
            setSelectedWorker(undefined);
            return;
          }
          setValue(newValue);
          setSelectedWorker(newValue);
        }}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue?.trim());
        }}
        renderInput={(params) => (
          <TextField {...params} label="Search for a Professional" fullWidth value={currentValue} />
        )}
        renderOption={(renderProps, nearbyWorker) => {
          return <NearbyWorkerResult renderProps={renderProps} nearbyWorker={nearbyWorker} />;
        }}
        onFocus={() => {
          addUserJourneyEvent("start typing", SEARCH_INPUT);
        }}
        onBlur={() => {
          addUserJourneyEvent("end typing", SEARCH_INPUT);
        }}
      />
      <Stack spacing={1} direction="row" justifyContent="flex-end">
        <Button
          onClick={() => {
            logUserJourney("exit", BACK_BUTTON);
            logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
              action: "MODAL_CLOSE",
              ...segmentProperties,
            });
            modalState.closeModal();
          }}
          variant="outlined"
          startIcon={<ArrowBackIosNewOutlinedIcon />}
        >
          Back
        </Button>
        <Button
          onClick={() => {
            addUserJourneyEvent("click", NEXT_BUTTON);
            setActiveStep(1);
            logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
              action: "WORKER_SELECTED",
              ...segmentProperties,
            });
            onClickNext();
          }}
          variant="contained"
          endIcon={<ArrowForwardIosOutlinedIcon />}
          disabled={!isDefined(selectedWorker)}
        >
          Next
        </Button>
      </Stack>
    </>
  );
}
